import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataProtection } from '@aptsi-types';
import { DataAssetMappingService } from '../../shared/services/data-protection-program.service';

export interface DataProtectionProgramCompletedViewDialogData {
  mappingId: string;
  dataAssetName?: string;
  dataAssetId?: string;
}

export interface CompletedProgramDetails {
  program: DataProtection | null;
  implementation: DataProtection | null;
  verification: DataProtection | null;
}

@Component({
  selector: 'app-data-protection-program-completed-view',
  templateUrl: './data-protection-program-completed-view.component.html',
  styleUrls: ['./data-protection-program-completed-view.component.scss']
})
export class DataProtectionProgramCompletedViewComponent implements OnInit {
  details: CompletedProgramDetails | null = null;
  isLoading = true;
  loadError = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: DataProtectionProgramCompletedViewDialogData,
    private readonly dialogRef: MatDialogRef<DataProtectionProgramCompletedViewComponent>,
    private readonly dataProtectionProgramService: DataAssetMappingService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.loadDetails();
  }

  async loadDetails(): Promise<void> {
    const mappingId = String(this.data?.mappingId ?? '').trim();
    this.loadError = '';

    if (!mappingId) {
      this.isLoading = false;
      this.loadError = 'The mapping ID is missing, so completed program details cannot be loaded.';
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.details = null;
    try {
      // The endpoint currently has an Observable<DataProtection[]> service signature,
      // while its actual response is an object with program/implementation/verification.
      const response: unknown = await this.dataProtectionProgramService
        .FetchCompletedProgramDetailsByMappingId(mappingId)
        .toPromise();

      this.details = this.normalizeDetailsResponse(response);
      if (!this.details) {
        this.loadError = 'The service did not return completed program details in the expected format.';
      }
    } catch (error) {
      console.error('Unable to load completed program details:', error);
      this.loadError = this.getErrorMessage(error, 'Unable to load completed program details. Please try again.');
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) {
      const values = value.map(item => String(item ?? '').trim()).filter(Boolean);
      return values.length ? values.join(', ') : '—';
    }
    const text = String(value).trim();
    return text || '—';
  }

  private normalizeDetailsResponse(raw: unknown): CompletedProgramDetails | null {
    let candidate: unknown = raw;

    for (let depth = 0; depth < 5; depth++) {
      if (Array.isArray(candidate)) {
        candidate = candidate.find(item => !!item && typeof item === 'object') ?? null;
      }
      if (!candidate || typeof candidate !== 'object') return null;

      const record = candidate as Record<string, unknown>;
      if ('program' in record || 'implementation' in record || 'verification' in record) {
        return {
          program: this.asDataProtection(record['program']),
          implementation: this.asDataProtection(record['implementation']),
          verification: this.asDataProtection(record['verification'])
        };
      }

      const wrapperKeys = ['data', 'result', 'details', 'completedProgramDetails', 'Resource retrieved successfully'];
      let wrapped: unknown;
      for (const key of wrapperKeys) {
        if (record[key] !== undefined && record[key] !== null) {
          wrapped = record[key];
          break;
        }
      }
      if (wrapped === undefined) return null;
      candidate = wrapped;
    }

    return null;
  }

  private asDataProtection(value: unknown): DataProtection | null {
    if (!value || typeof value !== 'object') return null;
    if (Array.isArray(value)) {
      const first = value.find(item => !!item && typeof item === 'object');
      return first ? first as DataProtection : null;
    }
    return value as DataProtection;
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error === 'object') {
      const value = error as Record<string, unknown>;
      const nested = value['error'];
      if (nested && typeof nested === 'object') {
        const message = (nested as Record<string, unknown>)['message'];
        if (message) return String(message);
      }
      if (value['message']) return String(value['message']);
    }
    return fallback;
  }
}
